/**
 * SelectionContextPill — the "highlight and ask" context, rendered above
 * the CommandRail input when the writer has text selected in Word.
 *
 * Communicates: "your selected text will be sent as context with
 * your next message." The writer can dismiss it per-message by
 * clicking ×.
 *
 * Renders as a ``QuotedSelection`` card (mono "SELECTED IN WORD"
 * caption + two-line excerpt), the same object the sent user bubble
 * shows above the question in the thread, so the writer sees the
 * selection travel from composer to conversation.
 *
 * # DECISION: quote card instead of a one-line italic pill
 * #   (2026-10-14, launch film step 6 "highlight and ask").
 * # WHY: a 60-char single-line pill hid most of a paragraph-length
 * #      highlight, so the writer could not confirm WHAT would be sent.
 * #      Two clamped lines (full text in the tooltip) are enough to
 * #      recognise the paragraph without crowding the composer.
 * # DATE: 2026-10-14
 *
 * SRP: pure presentation — no state, no Word.run, no effects.
 *      Caller owns capture + dismiss lifecycle.
 */

import { makeStyles } from "@fluentui/react-components";
import type React from "react";
import { brandColors, inkColors } from "../tokens";
import QuotedSelection from "./QuotedSelection";

// ---------------------------------------------------------------------------
// Styles
// ---------------------------------------------------------------------------

const useStyles = makeStyles({
  // Inset to line up with the suggestion chips and the input row below.
  row: {
    padding: "6px 12px 0",
  },
  dismissBtn: {
    display: "inline-flex",
    alignItems: "center",
    justifyContent: "center",
    flexShrink: 0,
    width: "16px",
    height: "16px",
    padding: 0,
    border: "none",
    borderRadius: "50%",
    background: "transparent",
    color: inkColors.ink4,
    fontSize: "9px",
    lineHeight: 1,
    cursor: "pointer",
    "&:hover": {
      background: brandColors.light,
      color: brandColors.primary,
    },
    "&:focus-visible": {
      outline: `2px solid ${brandColors.primary}`,
      outlineOffset: "1px",
    },
  },
});

// ---------------------------------------------------------------------------
// Props
// ---------------------------------------------------------------------------

export interface SelectionContextPillProps {
  /** The selected text to preview (always non-empty). */
  text: string;
  /** Called when the writer clicks the × dismiss button. */
  onDismiss: () => void;
}

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

export const SelectionContextPill: React.FC<SelectionContextPillProps> = ({ text, onDismiss }) => {
  const styles = useStyles();

  return (
    <div className={styles.row}>
      <QuotedSelection
        text={text}
        caption="Selected in Word · sent with your message"
        ariaLabel="Selected text will be sent as context"
        trailing={
          <button
            type="button"
            className={styles.dismissBtn}
            onClick={onDismiss}
            aria-label="Remove selection context"
            title="Remove — message will be sent without selection context"
          >
            ✕
          </button>
        }
      />
    </div>
  );
};
